(() => {
  const normalize = value => String(value || '').trim().replace(/[‐‑‒–—−]/g, '-').toLowerCase().replace(/\*$/, '');
  const normalizeFilter = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const isSku = value => /^[a-z]{1,16}-(?:\d[a-z0-9-]*)?$/i.test(normalize(value));
  const option = (variant, name) => variant.selectedOptions.find(item => item.name.toLowerCase() === name)?.value || '';
  const select = (products, query, params) => {
    const prefix = normalize(query);
    const selected = key => params.getAll(key);
    const types = selected('filter.p.product_type'), colors = selected('filter.v.option.color'), fabrics = selected('sv_fabric');
    const available = selected('filter.v.availability');
    const min = params.get('filter.v.price.gte'), max = params.get('filter.v.price.lte');
    const result = [...new Map(products.map(product => [product.id, product])).values()].flatMap(product => {
      if (types.length && !types.includes(normalizeFilter(product.productType))) return [];
      const variants = product.variants.nodes.filter(variant => normalize(variant.sku).startsWith(prefix)
        && (!colors.length || colors.includes(normalizeFilter(option(variant, 'color'))))
        && (!fabrics.length || fabrics.includes(normalizeFilter(option(variant, 'fabric'))))
        && (!available.length || available.includes(variant.availableForSale ? '1' : '0'))
        && (!min || Number(variant.price.amount) >= Number(min))
        && (!max || Number(variant.price.amount) <= Number(max)));
      if (!variants.length) return [];
      variants.sort((a, b) => Number(normalize(b.sku) === prefix) - Number(normalize(a.sku) === prefix)
        || Number(b.availableForSale) - Number(a.availableForSale)
        || Number(a.price.amount) - Number(b.price.amount)
        || a.sku.localeCompare(b.sku, undefined, { numeric: true }));
      return [{ product, variant: variants[0], price: Math.min(...variants.map(v => Number(v.price.amount))) }];
    });
    const sort = params.get('sort_by');
    result.sort((a, b) => {
      if (sort === 'price-ascending') return a.price - b.price || a.product.id.localeCompare(b.product.id);
      if (sort === 'price-descending') return b.price - a.price || a.product.id.localeCompare(b.product.id);
      if (sort === 'title-ascending') return a.product.title.localeCompare(b.product.title) || a.product.id.localeCompare(b.product.id);
      if (sort === 'title-descending') return b.product.title.localeCompare(a.product.title) || a.product.id.localeCompare(b.product.id);
      return Number(normalize(b.variant.sku) === prefix) - Number(normalize(a.variant.sku) === prefix)
        || Number(b.variant.availableForSale) - Number(a.variant.availableForSale)
        || a.variant.sku.localeCompare(b.variant.sku, undefined, { numeric: true }) || a.product.id.localeCompare(b.product.id);
    });
    return result;
  };
  const paginate = (items, requested) => {
    const pages = Math.max(1, Math.ceil(items.length / 24));
    const page = Math.min(pages, Math.max(1, Math.floor(Number(requested) || 1)));
    return { items: items.slice((page - 1) * 24, page * 24), page, pages, total: items.length, first: items.length ? (page - 1) * 24 + 1 : 0, last: Math.min(page * 24, items.length) };
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = { normalize, isSku, select, paginate }; return; }
  const configElement = document.getElementById('sv-sku-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const initial = new URL(location.href);
  if (!isSku(initial.searchParams.get('q')) || ['article', 'page'].includes(initial.searchParams.get('type'))) return;
  const root = document.querySelector('.sv-search'), main = root.querySelector('.sv-search__main'), count = root.querySelector('.sv-search__count-row');
  const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), config.labels[key] || key);
  const el = (tag, text, className) => { const node = document.createElement(tag); if (text !== undefined) node.textContent = text; if (className) node.className = className; return node; };
  const money = (amount, currency) => new Intl.NumberFormat(config.locale, { style: 'currency', currency }).format(Number(amount));
  const fields = 'id sku availableForSale price { amount currencyCode } compareAtPrice { amount } selectedOptions { name value }';
  const catalogQuery = `query Catalog($query:String,$after:String,$country:CountryCode,$language:LanguageCode) @inContext(country:$country,language:$language) { products(first:250,after:$after,query:$query,sortKey:ID) { pageInfo { hasNextPage endCursor } nodes { id handle title productType featuredImage { url altText } variants(first:10) { pageInfo { hasNextPage endCursor } nodes { ${fields} } } } } }`;
  const variantQuery = `query MoreVariants($id:ID!,$after:String,$country:CountryCode,$language:LanguageCode) @inContext(country:$country,language:$language) { product(id:$id) { variants(first:100,after:$after) { pageInfo { hasNextPage endCursor } nodes { ${fields} } } } }`;
  const cacheKey = `v2:${config.country}:${config.language}:${config.currency}`;
  let catalog, catalogPrefix = '', catalogComplete = false, database, loading = false;
  const db = () => database ||= new Promise(resolve => {
    try {
      const request = indexedDB.open('sv-sku-catalog', 1);
      request.onupgradeneeded = () => request.result.createObjectStore('catalog');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  const cache = async value => {
    const connection = await db();
    if (!connection) return null;
    return new Promise(resolve => {
      try {
        const transaction = connection.transaction('catalog', value ? 'readwrite' : 'readonly');
        const request = value ? transaction.objectStore('catalog').put(value, cacheKey) : transaction.objectStore('catalog').get(cacheKey);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
        transaction.onabort = () => resolve(null);
      } catch { resolve(null); }
    });
  };
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  const graphql = async (query, variables) => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(25000),
          body: JSON.stringify({ query, variables: { ...variables, country: config.country, language: config.language } })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (json.errors?.length || !json.data) throw new Error('Catalogue request failed');
        return json.data;
      } catch (error) { if (attempt === 2) throw error; await pause(700 * 2 ** attempt); }
    }
  };
  const loadCatalog = async (status, skipCache = false) => {
    const stored = skipCache ? null : await cache();
    if (stored && Date.now() - stored.time < 3600000 && Array.isArray(stored.products)) return stored.products;
    const now = new Date(), months = (now.getUTCFullYear() - 2024) * 12 + now.getUTCMonth() + 1;
    const ranges = ['created_at:<2024-01-01', ...Array.from({ length: Math.max(1, months) }, (_, index) => {
      const start = new Date(Date.UTC(2024, index, 1)).toISOString().slice(0, 10);
      const end = new Date(Date.UTC(2024, index + 1, 1)).toISOString().slice(0, 10);
      return `created_at:>=${start}${index < months - 1 ? ` AND created_at:<${end}` : ''}`;
    })];
    const products = new Map();
    let next = 0, failed = false;
    await Promise.all(Array.from({ length: 4 }, async () => {
      while (next < ranges.length && !failed) {
        const range = ranges[next++];
        let after = null;
        try {
          do {
            const { products: connection } = await graphql(catalogQuery, { query: range, after });
            connection.nodes.forEach(product => products.set(product.id, product));
            if (status?.isConnected) status.textContent = t('checking', { count: products.size.toLocaleString(config.locale) });
            const cursor = connection.pageInfo.endCursor;
            if (connection.pageInfo.hasNextPage && (!cursor || cursor === after)) throw new Error('Incomplete catalogue');
            after = connection.pageInfo.hasNextPage ? cursor : null;
          } while (after && !failed);
        } catch (error) { failed = true; throw error; }
      }
    }));
    for (const product of products.values()) {
      while (product.variants.pageInfo.hasNextPage) {
        const after = product.variants.pageInfo.endCursor;
        const data = await graphql(variantQuery, { id: product.id, after });
        if (!data.product) { products.delete(product.id); break; }
        const extra = data.product.variants;
        if (extra.pageInfo.hasNextPage && (!extra.pageInfo.endCursor || extra.pageInfo.endCursor === after)) throw new Error('Incomplete variants');
        product.variants.nodes.push(...extra.nodes);
        product.variants.pageInfo = extra.pageInfo;
      }
    }
    const complete = [...products.values()];
    void cache({ time: Date.now(), products: complete });
    return complete;
  };
  const loadSeed = async query => {
    const prefix = normalize(query).split('-')[0];
    const urls = config.skuAssets?.[prefix];
    if (!Array.isArray(urls) || !urls.length) return null;
    const chunks = await Promise.all(urls.map(async url => {
      const response = await fetch(url, { signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }));
    return chunks.every(Array.isArray) ? chunks.flat() : null;
  };
  const showLocation = async (scroll = false) => {
    const query = new URLSearchParams(location.search).get('q') || '';
    const prefix = normalize(query).split('-')[0];
    if (!catalogComplete && prefix !== catalogPrefix) {
      const seed = await loadSeed(query);
      if (seed?.length) { catalog = seed; catalogPrefix = prefix; }
    }
    render();
    if (scroll) { main.scrollIntoView({ behavior: 'auto', block: 'start' }); main.focus({ preventScroll: true }); }
  };
  const navigate = (url, scroll = false) => {
    history.pushState({ svSku: true }, '', url);
    void showLocation(scroll);
  };
  const render = () => {
    if (!catalog) return;
    const url = new URL(location.href), params = url.searchParams, query = params.get('q') || '';
    const matches = select(catalog, query, params), page = paginate(matches, params.get('page'));
    if (params.has('page') && Number(params.get('page')) !== page.page) { params.set('page', page.page); history.replaceState({ svSku: true }, '', url); }
    root.querySelector('h1').textContent = t('heading', { query });
    document.querySelectorAll('form input[name="q"]').forEach(input => { input.value = query; });
    const total = t('count', { count: page.total.toLocaleString(config.locale) });
    document.title = `${total} – ${query} – ${config.shop}`;
    count.replaceChildren(el('p', total, 'sv-search__count'));
    main.removeAttribute('aria-busy');
    main.replaceChildren();
    const layout = el('div', undefined, 'sv-search__results-layout');
    const filters = el('aside', undefined, 'sv-search__filters sv-sku-filters');
    filters.setAttribute('aria-label', t('filters'));
    const filterForm = el('div');
    filterForm.append(el('h2', t('filters')));
    const all = select(catalog, query, new URLSearchParams());
    const groupedValues = values => [...new Map(values.filter(Boolean).map(value => [normalizeFilter(value), String(value).trim()])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
    const choices = [
      ['filter.p.product_type', 'product_type', groupedValues(all.map(row => row.product.productType))],
      ['sv_fabric', 'fabric', groupedValues(all.flatMap(row => row.product.variants.nodes.filter(v => normalize(v.sku).startsWith(normalize(query))).map(v => option(v, 'fabric'))))],
      ['filter.v.option.color', 'color', groupedValues(all.flatMap(row => row.product.variants.nodes.filter(v => normalize(v.sku).startsWith(normalize(query))).map(v => option(v, 'color'))))]
    ];
    for (const [name, label, values] of choices) {
      if (!values.length) continue;
      const details = el('details'); details.open = true;
      details.append(el('summary', t(label)));
      for (const [value, display] of values) {
        const row = el('label'), input = el('input'); input.type = 'checkbox'; input.name = name; input.value = value; input.checked = params.getAll(name).includes(value);
        row.append(input, document.createTextNode(display)); details.append(row);
      }
      filterForm.append(details);
    }
    const stock = el('label'), checkbox = el('input'); checkbox.type = 'checkbox'; checkbox.name = 'filter.v.availability'; checkbox.value = '1'; checkbox.checked = params.getAll(checkbox.name).includes('1'); stock.append(checkbox, document.createTextNode(t('in_stock'))); filterForm.append(stock);
    for (const [name, label] of [['filter.v.price.gte', 'min_price'], ['filter.v.price.lte', 'max_price']]) {
      const row = el('label', t(label, { currency: config.currency })), input = el('input'); input.type = 'number'; input.step = '0.01'; input.min = '0'; input.name = name; input.value = params.get(name) || ''; row.append(input); filterForm.append(row);
    }
    const apply = el('button', t('apply')); apply.type = 'button'; filterForm.append(apply);
    const reset = el('button', t('clear')); reset.type = 'button'; reset.addEventListener('click', () => { [...params.keys()].forEach(key => { if (key.startsWith('filter.') || key === 'sv_fabric' || key === 'page') params.delete(key); }); navigate(url); }); filterForm.append(reset);
    apply.addEventListener('click', () => {
      [...params.keys()].forEach(key => { if (key.startsWith('filter.') || key === 'sv_fabric' || key === 'page') params.delete(key); });
      filterForm.querySelectorAll('input').forEach(input => {
        if (input.type === 'checkbox' && !input.checked) return;
        if (input.value) params.append(input.name, input.value);
      });
      navigate(url);
    });
    filters.append(filterForm); layout.append(filters);
    const column = el('div', undefined, 'sv-search__results-column'), toolbar = el('div', undefined, 'sv-search__toolbar');
    toolbar.append(el('p', t('range', { first: page.first, last: page.last, total: page.total }), 'sv-sku-range'));
    const sortLabel = el('label', t('sort')), sort = el('select'); sort.setAttribute('aria-label', t('sort'));
    for (const [value, label] of [['relevance', 'relevance'], ['price-ascending', 'price_asc'], ['price-descending', 'price_desc'], ['title-ascending', 'title_asc'], ['title-descending', 'title_desc']]) { const item = el('option', t(label)); item.value = value; item.selected = (params.get('sort_by') || 'relevance') === value; sort.append(item); }
    sort.addEventListener('change', () => { params.set('sort_by', sort.value); params.delete('page'); navigate(url); }); sortLabel.append(sort); toolbar.append(sortLabel); column.append(toolbar);
    const grid = el('div', undefined, 'sv-search__grid');
    for (const [cardIndex, { product, variant }] of page.items.entries()) {
      const card = el('article', undefined, 'sv-result-card sv-sku-card'); card.dataset.sku = variant.sku;
      const href = `${config.root}products/${encodeURIComponent(product.handle)}?variant=${variant.id.split('/').pop()}`;
      const imageLink = el('a', undefined, 'sv-result-card__image'); imageLink.href = href; imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden', 'true');
      if (product.featuredImage) {
        const image = el('img'), originalUrl = product.featuredImage.url, imageUrl = new URL(originalUrl);
        imageUrl.searchParams.set('width', '600');
        image.src = imageUrl.href; image.alt = product.featuredImage.altText || product.title;
        image.loading = cardIndex < 4 ? 'eager' : 'lazy'; image.decoding = 'async';
        if (cardIndex < 4) image.fetchPriority = 'high';
        image.width = 600; image.height = 450;
        image.addEventListener('error', () => {
          if (image.dataset.retried) { image.hidden = true; imageLink.classList.add('is-image-missing'); return; }
          image.dataset.retried = 'true'; image.src = originalUrl;
        });
        imageLink.append(image);
      }
      if (!variant.availableForSale) imageLink.append(el('b', t('sold_out'), 'sv-result-card__badge'));
      card.append(imageLink);
      const body = el('div', undefined, 'sv-result-card__body'), heading = el('h2'), link = el('a', product.title); link.href = href; heading.append(link);
      body.append(el('small', variant.sku), heading);
      const price = el('div', undefined, 'sv-result-card__price');
      if (Number(variant.compareAtPrice?.amount) > Number(variant.price.amount)) price.append(el('s', money(variant.compareAtPrice.amount, variant.price.currencyCode)));
      price.append(el('strong', money(variant.price.amount, variant.price.currencyCode))); body.append(price);
      const view = el('a', t('view'), 'sv-sku-view'); view.href = href; body.append(view);
      const form = el('form'); form.method = 'post'; form.action = config.cartAdd;
      const id = el('input'); id.type = 'hidden'; id.name = 'id'; id.value = variant.id.split('/').pop();
      const button = el('button', variant.availableForSale ? t('add') : t('sold_out')); button.type = 'submit'; button.disabled = !variant.availableForSale; form.append(id, button); body.append(form);
      card.append(body); grid.append(card);
    }
    if (!page.total) { const empty = el('section', undefined, 'sv-search__empty'); empty.append(el('h2', t('empty')), el('p', t('empty_help', { query }))); grid.append(empty); }
    column.append(grid);
    const pagination = el('nav', undefined, 'sv-search__pagination'); pagination.setAttribute('aria-label', t('pagination'));
    const pageLink = (number, label) => { const target = new URL(url); target.searchParams.set('page', number); const link = el('a', label); link.href = target.href; link.addEventListener('click', event => { if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; event.preventDefault(); navigate(target, true); }); return link; };
    if (page.page > 1) pagination.append(pageLink(page.page - 1, t('previous')));
    pagination.append(el('span', t('pages', { page: page.page, pages: page.pages })));
    if (page.page < page.pages) pagination.append(pageLink(page.page + 1, t('next')));
    column.append(pagination); layout.append(column); main.append(layout);
  };
  const start = async () => {
    if (loading) return;
    loading = true;
    main.tabIndex = -1;
    try {
      const query = new URLSearchParams(location.search).get('q') || '';
      const seed = await loadSeed(query);
      if (seed?.length) {
        catalog = seed;
        catalogPrefix = normalize(query).split('-')[0];
        render();
      } else {
        main.setAttribute('aria-busy', 'true');
        count.replaceChildren(el('p', t('loading'), 'sv-search__count'));
        document.title = `${t('loading')} – ${config.shop}`;
        const status = el('p', t('loading')), panel = el('section', undefined, 'sv-search__empty');
        panel.setAttribute('role', 'status'); panel.append(status, el('p', t('loading_help'))); main.replaceChildren(panel);
        catalog = await loadCatalog(status, true);
        catalogComplete = true;
        render();
      }
    }
    catch { main.removeAttribute('aria-busy'); count.replaceChildren(el('p', t('error'), 'sv-search__count')); document.title = `${t('error')} – ${config.shop}`; }
    finally { loading = false; }
  };
  root.classList.add('sv-sku-active');
  root.querySelectorAll('.sv-search__tabs a').forEach(link => {
    const target = new URL(link.href);
    target.searchParams.set('q', initial.searchParams.get('q'));
    link.href = target.href;
    link.classList.toggle('is-active', target.searchParams.get('type') === 'product');
  });
  window.addEventListener('popstate', event => {
    if (!isSku(new URLSearchParams(location.search).get('q'))) { location.reload(); return; }
    event.stopImmediatePropagation(); void showLocation();
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('input[name="q"]') || !form.action.includes('/search')) return;
    const query = form.querySelector('input[name="q"]').value;
    if (!isSku(query)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const url = new URL(form.action); url.searchParams.set('q', query.trim()); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last'); navigate(url);
  }, true);
  start();
})();

